import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  getRootExpenses,
  deleteExpense,
  updateExpense,
  createExpense, // ✅ استدعاء دالة الإضافة
} from "../../../../services/Accounts/Expenses";
import { getBanks } from "../../../../services/dashboard/banks";
import { useFeatures } from "../../../../context/PermissionsAndFeatures/FeaturesContext";

const ExpensesList = () => {
  const [expenses, setExpenses] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState(""); // شريط البحث
  const [dateFrom, setDateFrom] = useState(""); // تاريخ من
  const [dateTo, setDateTo] = useState(""); // تاريخ إلى

  // ✅ حالة للتعديل والإضافة
  const [openEdit, setOpenEdit] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [newExpense, setNewExpense] = useState({
    root_id: localStorage.getItem("branch_id") || "", // 👈 ياخد الفرع الحالي
    bank_id: "",
    amount: "",
    expense_date: "",
    description: "",
    title: "",
    file: null,
  });
  const [filePreview, setFilePreview] = useState(null);

  const IMAGE_PLACEHOLDER = "https://via.placeholder.com/80x80?text=No+File";

  // استخلاص حالة ميزة المصروفات
  const { hasFeature, featuresLoading } = useFeatures();
  const expensesEnabled = !featuresLoading && hasFeature("expenses");

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const res = await getRootExpenses();
      setExpenses(res.data || []);
      setError(null);
    } catch (err) {
      setError("❌ فشل في تحميل المصروفات");
    } finally {
      setLoading(false);
    }
  };

  const fetchBanks = async () => {
    try {
      const banksList = await getBanks();
      setBanks(banksList);
    } catch (err) {
      console.error("❌ فشل في تحميل الخزن:", err);
    }
  };

  useEffect(() => {
    fetchExpenses();
    fetchBanks();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("هل أنت متأكد من حذف هذا المصروف؟")) return;
    try {
      await deleteExpense(id);
      fetchExpenses();
    } catch {
      alert("❌ فشل في حذف المصروف");
    }
  };

  // ✅ فتح نافذة التعديل
  const handleEdit = async (exp) => {
    setSelectedExpense({
      ...exp,
      bank_id: exp.bank?.id || "",
    });
    await fetchBanks();
    setOpenEdit(true);
  };

  // ✅ تنفيذ التعديل
  const handleUpdate = async () => {
    try {
      await updateExpense(selectedExpense.id, {
        bank_id: selectedExpense.bank_id,
        amount: selectedExpense.amount,
        expense_date: selectedExpense.expense_date,
        description: selectedExpense.description,
        title: selectedExpense.title,
        file: selectedExpense.file || null,
      });
      setOpenEdit(false);
      fetchExpenses();
    } catch {
      alert("❌ فشل في تعديل المصروف");
    }
  };

  // ✅ تنفيذ الإضافة
  const handleAdd = async () => {
    try {
      await createExpense(newExpense);
      setOpenAdd(false);
      fetchExpenses();
    } catch {
      alert("❌ فشل في إضافة المصروف");
    }
  };

  // فلترة وترتيب المصروفات حسب البحث والتاريخ
  const filteredExpenses = expenses
    .filter(
      (exp) =>
        (exp.title?.toLowerCase().includes(search.toLowerCase()) ||
          exp.description?.toLowerCase().includes(search.toLowerCase())) &&
        (!dateFrom || new Date(exp.expense_date) >= new Date(dateFrom)) &&
        (!dateTo || new Date(exp.expense_date) <= new Date(dateTo))
    )
    .sort(
      (a, b) =>
        new Date(b.expense_date).getTime() - new Date(a.expense_date).getTime()
    );

  // حساب إجمالي المبلغ المعروض
  const totalAmount = filteredExpenses.reduce(
    (sum, exp) => sum + Number(exp.amount || 0),
    0
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setNewExpense({ ...newExpense, file });
    if (file) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(ext)) {
        setFilePreview(URL.createObjectURL(file));
      } else if (ext === "pdf") {
        setFilePreview("pdf");
      } else {
        setFilePreview("file");
      }
    } else {
      setFilePreview(null);
    }
  };

  // عند إغلاق نافذة الإضافة امسح المعاينة
  useEffect(() => {
    if (!openAdd) setFilePreview(null);
  }, [openAdd]);

  // أثناء تحميل حالة الميزات: إظهار سبينر
  if (featuresLoading) {
    return <CircularProgress sx={{ display: "block", mx: "auto", mt: 4 }} />;
  }

  // لو الميزة غير مفعّلة: اعرض رسالة و عطل إمكانية الإضافة/العرض
  if (!expensesEnabled) {
    return (
      <Box sx={{ p: 3, textAlign: "center", minHeight: "50vh" }}>
        <Typography variant="h6" color="error" gutterBottom>
          🚫 ميزة إدارة المصروفات (expenses) غير مفعّلة في حسابك. لا يمكنك عرض أو إضافة مصروفات.
        </Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} disabled>
          إضافة مصروف
        </Button>
      </Box>
    );
  }

  if (loading)
    return <CircularProgress sx={{ display: "block", mx: "auto", mt: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">💰 قائمة المصروفات</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setOpenAdd(true)}
        >
          إضافة مصروف
        </Button>
      </Box>
      {/* فلاتر التاريخ والبحث */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          label="من تاريخ"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="إلى تاريخ"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          label="🔍 بحث عن مصروف..."
          variant="outlined"
          fullWidth
          sx={{ flex: 1, minWidth: 200 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
        />
      </Box>
      {/* عرض إجمالي المبلغ */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" color="primary">
          إجمالي المصروفات المعروضة: {totalAmount.toLocaleString()} ج.م
        </Typography>
      </Box>
      <Paper>
        {expenses.length === 0 ? (
          <Alert severity="info">لا توجد مصروفات مسجلة.</Alert>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>📌 العنوان</TableCell>
                <TableCell>💵 المبلغ</TableCell>
                <TableCell>🏦 الخزنة</TableCell>
                <TableCell>👤 المستخدم</TableCell>
                <TableCell>📅 التاريخ</TableCell>
                <TableCell>📎 الملف</TableCell>
                <TableCell>✏️ العمليات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredExpenses.map((exp) => {
                const fileUrl = getFileUrl(exp.file);
                const ext = exp.file
                  ? exp.file.split(".").pop().toLowerCase()
                  : "";
                const isImage = ["jpg", "jpeg", "png", "gif", "webp"].includes(
                  ext
                );
                const isPdf = ext === "pdf";

                return (
                  <TableRow key={exp.id}>
                    <TableCell>{exp.title}</TableCell>
                    <TableCell>{exp.amount} ج.م</TableCell>
                    <TableCell>{exp.bank?.name || "غير محدد"}</TableCell>
                    <TableCell>{exp.user?.name || "غير محدد"}</TableCell>
                    <TableCell>
                      {new Date(exp.expense_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {fileUrl ? (
                        isImage ? (
                          <img
                            src={fileUrl}
                            alt="file"
                            style={{
                              width: 60,
                              height: 60,
                              objectFit: "cover",
                              borderRadius: 6,
                            }}
                          />
                        ) : isPdf ? (
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <PictureAsPdfIcon
                              sx={{ fontSize: 40, color: "red" }}
                            />
                          </a>
                        ) : (
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <InsertDriveFileIcon
                              sx={{ fontSize: 40, color: "gray" }}
                            />
                          </a>
                        )
                      ) : (
                        <img src={IMAGE_PLACEHOLDER} alt="no file" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="تعديل">
                        <IconButton
                          color="primary"
                          onClick={() => handleEdit(exp)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف">
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(exp.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* ✅ نافذة الإضافة */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)}>
        <DialogTitle>➕ إضافة مصروف جديد</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
        >
          <TextField
            label="العنوان"
            value={newExpense.title}
            onChange={(e) =>
              setNewExpense({ ...newExpense, title: e.target.value })
            }
          />
          <TextField
            label="الوصف"
            value={newExpense.description}
            onChange={(e) =>
              setNewExpense({ ...newExpense, description: e.target.value })
            }
          />
          <TextField
            label="المبلغ"
            type="number"
            value={newExpense.amount}
            onChange={(e) =>
              setNewExpense({ ...newExpense, amount: e.target.value })
            }
          />
          <TextField
            label="التاريخ"
            type="date"
            value={newExpense.expense_date}
            onChange={(e) =>
              setNewExpense({ ...newExpense, expense_date: e.target.value })
            }
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth>
            <InputLabel id="bank-add-label">🏦 اختر الخزنة</InputLabel>
            <Select
              labelId="bank-add-label"
              value={newExpense.bank_id}
              onChange={(e) =>
                setNewExpense({ ...newExpense, bank_id: e.target.value })
              }
            >
              {banks.map((bank) => (
                <MenuItem key={bank.id} value={bank.id}>
                  {bank.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" component="label">
            📎 رفع ملف
            <input
              type="file"
              hidden
              onChange={handleFileChange}
              accept=".pdf,.png,.jpg,.jpeg,.bmp,.gif,.webp"
            />
          </Button>
          {/* عرض معاينة الملف */}
          {filePreview && (
            <Box sx={{ mt: 1 }}>
              {filePreview === "pdf" ? (
                <Typography color="text.secondary" fontSize={14}>
                  <PictureAsPdfIcon
                    sx={{ color: "red", verticalAlign: "middle" }}
                  />{" "}
                  ملف PDF تم اختياره
                </Typography>
              ) : filePreview === "file" ? (
                <Typography color="text.secondary" fontSize={14}>
                  <InsertDriveFileIcon
                    sx={{ color: "gray", verticalAlign: "middle" }}
                  />{" "}
                  ملف تم اختياره
                </Typography>
              ) : (
                <img
                  src={filePreview}
                  alt="معاينة الملف"
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: "cover",
                    borderRadius: 6,
                    border: "1px solid #eee",
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdd(false)}>إلغاء</Button>
          <Button onClick={handleAdd} variant="contained" color="primary">
            إضافة
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ نافذة التعديل */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)}>
        <DialogTitle>تعديل المصروف</DialogTitle>
        <DialogContent
          sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
        >
          <TextField
            label="العنوان"
            value={selectedExpense?.title || ""}
            onChange={(e) =>
              setSelectedExpense({ ...selectedExpense, title: e.target.value })
            }
          />
          <TextField
            label="الوصف"
            value={selectedExpense?.description || ""}
            onChange={(e) =>
              setSelectedExpense({
                ...selectedExpense,
                description: e.target.value,
              })
            }
          />
          <TextField
            label="المبلغ"
            type="number"
            value={selectedExpense?.amount || ""}
            onChange={(e) =>
              setSelectedExpense({ ...selectedExpense, amount: e.target.value })
            }
          />
          <TextField
            label="التاريخ"
            type="date"
            value={selectedExpense?.expense_date || ""}
            onChange={(e) =>
              setSelectedExpense({
                ...selectedExpense,
                expense_date: e.target.value,
              })
            }
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth>
            <InputLabel id="bank-edit-label">🏦 اختر الخزنة</InputLabel>
            <Select
              labelId="bank-edit-label"
              value={selectedExpense?.bank_id || ""}
              onChange={(e) =>
                setSelectedExpense({
                  ...selectedExpense,
                  bank_id: e.target.value,
                })
              }
            >
              {banks.map((bank) => (
                <MenuItem key={bank.id} value={bank.id}>
                  {bank.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* يمكن إضافة رفع ملف جديد هنا إذا أردت */}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>إلغاء</Button>
          <Button onClick={handleUpdate} variant="contained" color="primary">
            حفظ التعديلات
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpensesList;

// ضع هنا رابط السيرفر الخاص بك أو اجلبه من متغير بيئة

// تعديل رابط السيرفر حسب بيئتك
const BASE_URL = `https://${
  window.location.hostname.split(".")[0]
}.restocash.app/`;

function getFileUrl(filePath) {
  if (!filePath) return "";
  if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
    return filePath;
  }
  return BASE_URL.replace(/\/$/, "") + "/" + filePath.replace(/^\//, "");
}
